import { Link } from "react-router-dom";
import { Check, Phone, Utensils, DraftingCompass, Lightbulb } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal, SectionLabel } from "@/components/Motion";
import { Ribbon } from "@/components/Ribbon";
import { BRAND, IMAGES } from "@/lib/site";

const credentials = [
  "Licensed Electrical Contractor — #886996",
  "Lutron Pro Certified",
  "Advises Residential, Commercial & Industrial clients",
  "Go Green Financing partner",
  "Serving Los Angeles & Greater Southern California",
  "Flexible advisory scheduling",
];

export default function About() {
  return (
    <div>
      <PageHeader
        label="About"
        titleLines={["Manny", "Diaz"]}
        sub="The expert you consult before you build. Four decades of turning electrical problems into simple, safe, code-compliant answers."
      />

      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10 md:pb-32">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl border border-amber/30" />
              <div className="relative overflow-hidden rounded-2xl">
                <img src={IMAGES.manny} alt="Manny Diaz" className="aspect-[4/5] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/60 to-transparent" />
              </div>
              <div className="absolute bottom-6 left-6 rounded-xl bg-amber px-5 py-3">
                <div className="font-head text-3xl font-semibold leading-none text-black">40+</div>
                <div className="text-[10px] font-medium tracking-widest text-black/70">Years experience</div>
              </div>
            </div>
          </Reveal>

          <div>
            <SectionLabel>The Story</SectionLabel>
            <Reveal>
              <h2 className="mt-6 font-head text-4xl font-medium leading-tight tracking-tight text-white md:text-5xl">
                Experience you can actually talk to
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-5 text-lg leading-relaxed text-white/60">
                <p>
                  Over 40 years, Manny has wired everything from Los Angeles estates to restaurants, retail build-outs and full industrial facilities. He's coordinated with Southern California Edison, pulled permits, and solved the faults other electricians walked away from.
                </p>
                <p>
                  Today he offers that experience as advisory only. You get his judgment — the diagnosis, the plan, the honest numbers — before a single wire is pulled, plus a plan you fully own and can take to any contractor. It's the fastest way to save money and avoid the expensive mistakes.
                </p>
              </div>
            </Reveal>

            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {credentials.map((c, i) => (
                <Reveal key={c} delay={i * 0.05}>
                  <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-ink-700 p-4">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber" strokeWidth={3} />
                    <span className="text-sm text-white/75">{c}</span>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-3 rounded-full bg-amber px-7 py-4 font-medium uppercase tracking-[0.2em] text-black transition-transform duration-300 hover:scale-[1.04]">
                Book a Consult
              </Link>
              <a href={BRAND.phoneHref} className="inline-flex items-center gap-3 rounded-full border border-white/25 px-7 py-4 font-medium uppercase tracking-[0.2em] text-white transition-colors hover:border-amber hover:text-amber">
                <Phone className="h-4 w-4" />{BRAND.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="value-highlights" className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10 md:pb-32">
        <SectionLabel>Where Manny Saves You Money</SectionLabel>

        <Reveal>
          <div className="mt-6 overflow-hidden rounded-2xl border border-amber/50 bg-amber/[0.06] p-8 md:p-12">
            <div className="flex flex-col gap-6 md:flex-row md:items-center">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-amber text-black">
                <DraftingCompass className="h-8 w-8" />
              </span>
              <p className="font-head text-3xl font-medium leading-[1.05] tracking-tight text-white md:text-5xl">
                Manny works <span className="text-amber">alongside architects</span> from day one — designing it right so you avoid maintenance calls and overspending later.
              </p>
            </div>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/60">
              Bringing an electrical expert into the design phase is the single biggest way to protect a budget. Manny catches the costly mistakes before they're built in — the ones that turn into service calls, rework and blown budgets down the road.
            </p>
          </div>
        </Reveal>

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Reveal delay={0.05}>
            <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-ink-700 p-8 transition-colors duration-300 hover:border-amber/40">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber/15 text-amber">
                <Utensils className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-head text-3xl font-medium tracking-tight text-white">Restaurants, done right</h3>
              <p className="mt-3 leading-relaxed text-white/60">
                Manny advises on <span className="font-semibold text-white">restaurant projects</span> — from planning new openings to diagnosing what's already there. Kitchens, equipment loads and inspection strategy mapped out so your doors stay open and your business keeps running.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-ink-700 p-8 transition-colors duration-300 hover:border-amber/40">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber/15 text-amber">
                <Lightbulb className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-head text-3xl font-medium tracking-tight text-white">Smarter lighting, real savings</h3>
              <p className="mt-3 leading-relaxed text-white/60">
                <span className="font-semibold text-white">Save money on lighting systems</span> instead of paying for very expensive ones. Manny helps you get the look and performance you want using cost-effective lighting — no premium price tag required.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <Ribbon direction="right" />
    </div>
  );
}
